import axios from "axios";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { getJsonResult } from "../../services/FetchServices";

interface AuthContextType {
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  roles: string[];
  showSupplierPayments: boolean;
  vendorId: string;
  userName: string;
  isLoading: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context == null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [roles, setRoles] = useState<string[]>([]);
  const [vendorId, setVendorId] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [showSupplierPayments, setShowSupplierPayments] =
    useState<boolean>(false);

  useEffect(() => {
    const initializeAuthAndConfigs = async () => {
      setIsLoading(true);

      try {
        const authUrl = `${process.env.REACT_APP_BACKEND_URL}/login/authStatus`;
        const configsUrl = `${process.env.REACT_APP_BACKEND_URL}/cvpConfigs`;

        const [authResponse, configsResponse] = await Promise.allSettled([
          getJsonResult(authUrl),
          axios.get(configsUrl),
        ]);

        if (authResponse.status === "fulfilled") {
          const isAuth = await authResponse.value.json();
          if (isAuth) {
            const userUrl = `${process.env.REACT_APP_BACKEND_URL}/api/userInfo`;
            const userResponse = await getJsonResult(userUrl);
            const data = await userResponse.json();

            localStorage.setItem("vendorId", data.vendorId ?? "");
            localStorage.setItem("userName", data.userName ?? "");

            setRoles(data.roles ?? []);
            setVendorId(data.vendorId ?? "");
            setUserName(data.userName ?? "");
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
            setRoles([]);
          }
        } else {
          setIsAuthenticated(false);
          setRoles([]);
        }

        if (
          configsResponse.status === "fulfilled" &&
          configsResponse.value.data?.length > 0
        ) {
          const rawValue = configsResponse.value.data[0];

          const isEnabled =
            rawValue === true ||
            (typeof rawValue === "string" &&
              rawValue.trim().toLowerCase() === "true");

          setShowSupplierPayments(isEnabled);
        } else {
          setShowSupplierPayments(false);
        }
      } catch (err) {
        console.error("Initialization error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuthAndConfigs();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        roles,
        showSupplierPayments,
        vendorId,
        userName,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
